// @dynamic-i18n-keys
// Dynamic t() at line 69: key from getBodyLocaleKey(isMobile, isSubscribedButBlocked).
//   deviationview.subpaywall.body
//   deviationview.subpaywall.body.subscribedButBlocked
//   deviationview.subpaywall.body.mobile
//   deviationview.subpaywall.body.subscribedButBlocked.mobile
import React, { useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import VideoIcon from '@wix/da-ds/pkg/Icons/24x24/AddVideo';
import LockIcon from '@wix/da-ds/pkg/Icons/24x24/Lock';
import { ThemeOverride, ThemeOptions } from '@wix/da-react-context/pkg/ThemeContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import ConditionalWrapper from '@wix/da-ds/pkg/ConditionalWrapper';
import { useDeviationViewContext } from '../../../../Context';
import { DeviationTypes } from '../../../../types/deviation';
import { usePrimaryTier } from '../../../../Tier/PrimaryTierContext';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import SubscribeButton from '../../../../Button/SubscribeButton';
import { getDeviationType, scaleToSize } from '../../../_helpers';
import { formatPrice } from '../../../TierProductCardDumb/utils';
import { useFullview } from '../../hooks/useFullview';
import Image from '../../Image';
import { getDeviationSaleDetails } from '../../../../utils/deviations';
import DiscountsPopupTrigger from '../../../../DiscountsPopupTrigger';

import s from './SubPaywall.scss';

interface Props {
  deviation: PapiDeviation;
  width?: number;
  height?: number;
  showImage?: boolean;
  isStatusArtStage?: boolean;
  className?: string;
}

const SubPaywall: React.FC<React.PropsWithChildren<Props>> = ({
  width,
  height,
  deviation,
  showImage = false,
  isStatusArtStage = false,
  className,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { isFullscreen } = useDeviationViewContext();
  const tier = usePrimaryTier(deviation);
  const { isSubscribedButBlocked } = useSubscriptionTier(deviation);
  const { previewFile } = useFullview({
    stageWidth: width ?? 0,
    stageHeight: height ?? 0,
    source: deviation,
    allowFullviewSrcsetForPngs: deviation.isPublished ?? false,
  });

  const { isDiscountApplied, allDiscounts, eligibleDiscount } = getDeviationSaleDetails(deviation);

  if (!tier) {
    return null;
  }

  const size = scaleToSize(previewFile ?? { width, height }, { width, height });
  const isFilm = getDeviationType(deviation) === DeviationTypes.FILM;
  const bodylocaleKey = getBodyLocaleKey(isMobile, isSubscribedButBlocked);
  const bodyText = t(bodylocaleKey, {
    username: deviation?.author?.username,
    price: formatPrice(tier.tier?.dollarPrice),
  });

  return (
    <div
      className={classNames(
        s['root'],
        !showImage && s['no-image'],
        isStatusArtStage && s['status'],
        isFullscreen && s['fullscreen'],
        className
      )}
      style={{ width, height }}
    >
      {showImage && (
        <div className={s['overlay']}>
          <div>
            <Image
              deviation={deviation}
              width={size?.width}
              height={size?.height}
              isZoomed={false}
            />
          </div>
          {isFilm && <VideoIcon className={s['video-icon']} />}
        </div>
      )}

      <div className={s['centered']} style={size}>
        <ConditionalWrapper
          condition={showImage}
          wrap={children => <ThemeOverride theme={ThemeOptions.DARK}>{children}</ThemeOverride>}
        >
          <LockIcon />
        </ConditionalWrapper>
        <div className={s['title']}>
          {t(
            isSubscribedButBlocked
              ? 'deviationview.subpaywall.title.subscribedButBlocked'
              : 'deviationview.subpaywall.title'
          )}
        </div>
        <div className={s['main']}>{bodyText}</div>

        {!isSubscribedButBlocked && (
          <BiLoggerContextProvider value={{ referral_info: ReferralType.CONTENT_PAGE }}>
            <SubscribeButton tier={tier} withSaleIndicator={isDiscountApplied} />
            <DiscountsPopupTrigger
              className={s['discounts-popup-trigger']}
              allDiscounts={allDiscounts}
              eligibleDiscount={eligibleDiscount}
            />
          </BiLoggerContextProvider>
        )}
      </div>
    </div>
  );
};
SubPaywall.displayName = 'SubPaywall';

export default SubPaywall;

function getBodyLocaleKey(isMobile = false, isSubscribedButBlocked = false): string {
  let bodyTextlocaleKey = 'deviationview.subpaywall.body';
  if (isSubscribedButBlocked) {
    // deviationview.subpaywall.body.subscribedButBlocked
    bodyTextlocaleKey += '.subscribedButBlocked';
  }
  if (isMobile) {
    // deviationview.subpaywall.body.mobile
    // deviationview.subpaywall.body.subscribedButBlocked.mobile
    bodyTextlocaleKey += '.mobile';
  }
  return bodyTextlocaleKey;
}
